import { Messages } from "../strings/strings-en-us";

// Human copy for the `Error` reason on a connector-auth v2 response.
//
// That field carries three different kinds of value and only one of them is ours:
//
//   1. a code minted by the ConnectorAuth library (invalid_json_path, missing_refresh_token, ...)
//      or by the connector-settings service (token_exchange_failed, ...);
//   2. a code the OAuth provider sent back verbatim, which is whatever that provider felt like
//      (OAuth2Helper.cs copies the token endpoint's `error` straight through);
//   3. a plain sentence, from the v2 controllers that predate any of this
//      ("Connector auth not found").
//
// Only (1) is a closed set, so only (1) is curated. Everything else is drawn as-is.
//
// The map is deliberately not a de-snaking regex. It exists to fix the casing a mechanical
// transform gets wrong (JSON, URI, JWT, HTTP) — where it has nothing to add it adds nothing,
// and it never rewrites a code it does not recognise. A transform that turns AADSTS50011 into
// "Aadsts50011" costs the reader the exact string they would have searched for, and we are
// already in a failure: the expensive mistake here is trying too hard, not too little.
//
// `ErrorDescription` is rendered separately and is where the provider's detail lives. Neither
// line is suppressed in favour of the other.

/** Every reason this module curates. Codes are lower-case snake_case, matched case-insensitively. */
export const ConnectorAuthErrorReason = {
    // Minted by the ConnectorAuth library.
    EmptyPlaceholder: "empty_placeholder",
    HttpError: "http_error",
    HttpSendError: "http_send_error",
    InvalidConfiguration: "invalid_configuration",
    InvalidFieldValue: "invalid_field_value",
    InvalidJsonPath: "invalid_json_path",
    InvalidJsonResponse: "invalid_json_response",
    InvalidParameter: "invalid_parameter",
    InvalidRegex: "invalid_regex",
    InvalidRequest: "invalid_request",
    InvalidResponse: "invalid_response",
    InvalidScheme: "invalid_scheme",
    InvalidServiceAccountJson: "invalid_service_account_json",
    InvalidState: "invalid_state",
    InvalidUri: "invalid_uri",
    JwtCreationError: "jwt_creation_error",
    MissingPlaceholder: "missing_placeholder",
    MissingRefreshToken: "missing_refresh_token",
    MissingRequiredField: "missing_required_field",
    MissingState: "missing_state",
    SessionError: "session_error",
    UnknownError: "unknown_error",

    // Minted by the connector-settings service only (it keeps its own copy of ConnectorAuthError,
    // and that copy has diverged).
    ContentReadError: "content_read_error",
    InvalidResponsePath: "invalid_response_path",
    RefreshFailed: "refresh_failed",
    TokenExchangeFailed: "token_exchange_failed",

    // Minted by the Popdock.Web v2 controllers, or by the flow hooks as a fallback.
    AuthorizationFailed: "authorization_failed",
    ConnectFailed: "connect_failed",
    CreateFailed: "create_failed",
    SessionExpired: "session_expired",
    ValidationFailed: "validation_failed",

    // Passed straight through from the OAuth provider. Not exhaustive and cannot be — this is
    // the RFC 6749 set plus the few extras that actually reach us.
    AccessDenied: "access_denied",
    InvalidClient: "invalid_client",
    InvalidGrant: "invalid_grant",
    InvalidScope: "invalid_scope",
    ReauthRequired: "reauth_required",
    ServerError: "server_error",
    TemporarilyUnavailable: "temporarily_unavailable",
    UnauthorizedClient: "unauthorized_client",
    UnsupportedGrantType: "unsupported_grant_type",
};

// Thunks, not values: a plain `Messages.X` here would resolve once at import and keep rendering
// that locale's copy after a switch.
const CURATED = {
    [ConnectorAuthErrorReason.AccessDenied]: () => Messages.ConnectorAuthErrorAccessDenied,
    [ConnectorAuthErrorReason.AuthorizationFailed]: () => Messages.AuthorizationFailed,
    [ConnectorAuthErrorReason.ConnectFailed]: () => Messages.ConnectionFailed,
    [ConnectorAuthErrorReason.ContentReadError]: () => Messages.ConnectorAuthErrorContentReadError,
    [ConnectorAuthErrorReason.CreateFailed]: () => Messages.ConnectorAuthErrorCreateFailed,
    [ConnectorAuthErrorReason.EmptyPlaceholder]: () => Messages.ConnectorAuthErrorEmptyPlaceholder,
    [ConnectorAuthErrorReason.HttpError]: () => Messages.ConnectorAuthErrorHttpError,
    [ConnectorAuthErrorReason.HttpSendError]: () => Messages.ConnectorAuthErrorHttpSendError,
    [ConnectorAuthErrorReason.InvalidClient]: () => Messages.ConnectorAuthErrorInvalidClient,
    [ConnectorAuthErrorReason.InvalidConfiguration]: () => Messages.ConnectorAuthErrorInvalidConfiguration,
    [ConnectorAuthErrorReason.InvalidFieldValue]: () => Messages.ConnectorAuthErrorInvalidFieldValue,
    [ConnectorAuthErrorReason.InvalidGrant]: () => Messages.ConnectorAuthErrorInvalidGrant,
    [ConnectorAuthErrorReason.InvalidJsonPath]: () => Messages.ConnectorAuthErrorInvalidJsonPath,
    [ConnectorAuthErrorReason.InvalidJsonResponse]: () => Messages.ConnectorAuthErrorInvalidJsonResponse,
    [ConnectorAuthErrorReason.InvalidParameter]: () => Messages.ConnectorAuthErrorInvalidParameter,
    [ConnectorAuthErrorReason.InvalidRegex]: () => Messages.ConnectorAuthErrorInvalidRegex,
    [ConnectorAuthErrorReason.InvalidRequest]: () => Messages.ConnectorAuthErrorInvalidRequest,
    [ConnectorAuthErrorReason.InvalidResponse]: () => Messages.ConnectorAuthErrorInvalidResponse,
    [ConnectorAuthErrorReason.InvalidResponsePath]: () => Messages.ConnectorAuthErrorInvalidResponsePath,
    [ConnectorAuthErrorReason.InvalidScheme]: () => Messages.ConnectorAuthErrorInvalidScheme,
    [ConnectorAuthErrorReason.InvalidScope]: () => Messages.ConnectorAuthErrorInvalidScope,
    [ConnectorAuthErrorReason.InvalidServiceAccountJson]: () => Messages.ConnectorAuthErrorInvalidServiceAccountJson,
    [ConnectorAuthErrorReason.InvalidState]: () => Messages.ConnectorAuthErrorInvalidState,
    [ConnectorAuthErrorReason.InvalidUri]: () => Messages.ConnectorAuthErrorInvalidUri,
    [ConnectorAuthErrorReason.JwtCreationError]: () => Messages.ConnectorAuthErrorJwtCreationError,
    [ConnectorAuthErrorReason.MissingPlaceholder]: () => Messages.ConnectorAuthErrorMissingPlaceholder,
    [ConnectorAuthErrorReason.MissingRefreshToken]: () => Messages.ConnectorAuthErrorMissingRefreshToken,
    [ConnectorAuthErrorReason.MissingRequiredField]: () => Messages.ConnectorAuthErrorMissingRequiredField,
    [ConnectorAuthErrorReason.MissingState]: () => Messages.ConnectorAuthErrorMissingState,
    [ConnectorAuthErrorReason.ReauthRequired]: () => Messages.ConnectorAuthErrorReauthRequired,
    [ConnectorAuthErrorReason.RefreshFailed]: () => Messages.ConnectorAuthErrorRefreshFailed,
    [ConnectorAuthErrorReason.ServerError]: () => Messages.ConnectorAuthErrorServerError,
    [ConnectorAuthErrorReason.SessionError]: () => Messages.ConnectorAuthErrorSessionError,
    [ConnectorAuthErrorReason.SessionExpired]: () => Messages.ConnectorAuthErrorSessionExpired,
    [ConnectorAuthErrorReason.TemporarilyUnavailable]: () => Messages.ConnectorAuthErrorTemporarilyUnavailable,
    [ConnectorAuthErrorReason.TokenExchangeFailed]: () => Messages.ConnectorAuthErrorTokenExchangeFailed,
    [ConnectorAuthErrorReason.UnauthorizedClient]: () => Messages.ConnectorAuthErrorUnauthorizedClient,
    [ConnectorAuthErrorReason.UnknownError]: () => Messages.ConnectorAuthErrorUnknownError,
    [ConnectorAuthErrorReason.UnsupportedGrantType]: () => Messages.ConnectorAuthErrorUnsupportedGrantType,
    [ConnectorAuthErrorReason.ValidationFailed]: () => Messages.ConnectorAuthErrorValidationFailed,
};

/**
 * Copy for a connector-auth error reason. Curated codes get their message; anything else —
 * an unfamiliar provider code, or a sentence a controller sent instead of a code — is returned
 * untouched rather than guessed at.
 *
 * Never throws and never returns a non-string: this runs while something is already broken, so
 * a reason that arrives as an object or a number must not take the error display down with it.
 * Assuming the field is a string is exactly the kind of assumption an error path cannot afford.
 *
 * @param {unknown} reason the `Error` field, or the `authError` URL param
 * @returns {string | null} null when there is no reason to show
 */
export function getConnectorAuthErrorMessage(reason) {
    if (!reason) return null;
    if (typeof reason !== "string") {
        // Not a code and not copy, but still the only information we have about the failure.
        try { return typeof reason === "object" ? JSON.stringify(reason) : String(reason); }
        catch { return String(reason); }
    }
    return CURATED[reason.toLowerCase()]?.() ?? reason;
}

/**
 * Both halves of a connector-auth failure in one line: the reason says which stage failed, the
 * description is the service's own account of why. Callers that had to pick one were dropping
 * the other — the reason alone loses the provider's detail, the description alone loses the
 * stage. Neither is redundant, so neither is discarded.
 *
 * @param {unknown} reason the `Error` field
 * @param {unknown} description the `ErrorDescription` field
 * @returns {string | null} null when there is nothing to show
 */
export function describeConnectorAuthError(reason, description) {
    const left = getConnectorAuthErrorMessage(reason);
    const right = getConnectorAuthErrorMessage(description);
    if (!left) return right;
    if (!right || right === left) return left;
    return `${left}: ${right}`;
}
